import { UserProfileModel } from "based-shared/models/UserProfile";
import { UserRole } from "based-shared/types";
import { AllChains, chainSolDevnet } from "based-auth/chains";
import { connectToDatabase } from "based-shared/utils/connect-database";
import shortid from "shortid";
import { createKeypairs } from "./create-keypairs";
import { auth } from "./auth-config";
import { NetworkSymbols } from "based-auth/networks";

const staticAdminAddresses = ["0x0000000000000000000000000000000000000000"];

const staticAdminEmails = ["talentdevstar@gmail.com"];

export async function updateOrCreateUserProfile(
  provider: string,
  chainId: number,
  network: string,
  address: string,
  social: string,
  name: string,
  image: string,
  role: UserRole
) {
  await connectToDatabase();

  const session = (await auth()) as any;
  let currentUser = null;
  if (session?.user?.id) {
    currentUser = await UserProfileModel.findById(session.user.id);
  }
  const existingUser = await UserProfileModel.findOne({
    "users.network": network,
    "users.address": address,
  });

  if (
    currentUser &&
    existingUser &&
    currentUser._id.toString() !== existingUser._id.toString()
  ) {
    const mergedUsers = [...currentUser.users, ...existingUser.users].filter(
      (user, idx, arr) =>
        arr.findIndex(
          (u) =>
            u.address.toLowerCase() === user.address.toLowerCase() &&
            u.network.toLowerCase() === user.network.toLowerCase()
        ) === idx
    );

    await UserProfileModel.deleteOne({ _id: existingUser._id });

    currentUser.users = mergedUsers;
    currentUser.lastAddress = address;
    currentUser.lastNetwork = network;
    await currentUser.save();

    return currentUser.toObject();
  }

  if (currentUser || existingUser) {
    const userProfile = currentUser || existingUser;
    if (!userProfile) {
      throw new Error("User profile not found");
    }
    if (
      !userProfile.users.some(
        (u: { address: string; network: string }) =>
          u.address.toLowerCase() === address.toLowerCase() &&
          u.network.toLowerCase() === network.toLowerCase()
      )
    ) {
      userProfile.users.push({
        address,
        network: network as NetworkSymbols,
        social,
        role,
        provider,
        chainId,
        image,
        name,
      });
    }
    userProfile.lastAddress = address;
    userProfile.lastNetwork = network;
    await userProfile.save();
    return userProfile.toObject();
  }

  const created = new UserProfileModel({
    name,
    image,
    lastAddress: address,
    lastNetwork: network,
    users: [{ address, network, chainId, social, role, provider, image, name }],
  });
  await created.save();

  return created.toObject();
}

export async function saveSocialUser(
  provider: string,
  social: string,
  name: string,
  image: string
) {
  let role = UserRole.USER;
  if (staticAdminEmails.includes(social)) {
    role = UserRole.ADMIN;
  }

  const chainId = Number(process.env.DEFAULT_CHAINID || chainSolDevnet.id);

  const chain = AllChains.find((c) => c.id === chainId);
  if (!chain) {
    throw new Error("Chain not found");
  }

  const network = chain.network.symbol;
  const { address } = createKeypairs(network, social, provider);
  if (staticAdminAddresses.includes(address)) {
    role = UserRole.ADMIN;
  }

  const user = await updateOrCreateUserProfile(
    provider,
    chain.id,
    network,
    address,
    social,
    name,
    image,
    role
  );

  return {
    _id: user.id,
    users: user.users,
    provider,
    chainId: chain.id,
    network,
    social,
    address,
    name,
    image,
    role,
  };
}

export async function saveWalletUser(
  provider: string,
  chainId: number,
  network: string,
  address: string
) {
  await connectToDatabase();

  let role = UserRole.USER;
  if (staticAdminAddresses.includes(address)) {
    role = UserRole.ADMIN;
  }

  const name = shortid.generate();
  const image = "https://api.dicebear.com/9.x/thumbs/svg?seed=" + address;

  const user = await updateOrCreateUserProfile(
    provider,
    chainId,
    network,
    address,
    "",
    name,
    image,
    role
  );

  return {
    _id: user.id,
    users: user.users,
    provider,
    chainId,
    network,
    social: "",
    address,
    name,
    image,
    role,
  };
}
